import {FC, useCallback, useEffect, useState} from 'react';
import { Modal } from 'antd';

import './preview-file-modal.scss'
import {DownloadOutlined, LeftOutlined, RightOutlined} from "@ant-design/icons";
import {CustomTooltip} from "@ppmcore/seven-ppm-core-shared-components-react";

interface IPreviewFileModalProps {
  open: boolean;
  title?: any;
  onCancel?: () => void;
  file?: File | null;
  fileUrl?: string; // URL from server
  fileName?: string;
  images?: File[];
  imageUrls?: string[];
  imageNames?: string[];
  currentIndex?: number;
  onIndexChange?: (index: number) => void;

}

export const PreviewFileModal: FC<IPreviewFileModalProps> = ({
   open = false,
   title = '',
   onCancel = () => {},
   file = null,
   fileUrl = '',
   fileName = 'download',
   images = [],
   imageUrls = [],
   imageNames = [],
   currentIndex = 0,
   onIndexChange = () => {}
 }: IPreviewFileModalProps) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [currentTitle, setCurrentTitle] = useState<string>('');

  const isSlider = images.length > 0 || imageUrls.length > 0;
  const totalImages = isSlider ? (images.length || imageUrls.length) : 1;
  const showNavigation = isSlider && totalImages > 1;

  useEffect(() => {
    if (!open) {
      setImageUrl('');
      setCurrentTitle('');
      return;
    }

    let cleanup: (() => void) | undefined;

    // Slider mode with File object
    if (images.length > 0 && currentIndex >= 0 && currentIndex < images.length) {
      const currentFile = images[currentIndex];
      const url = URL.createObjectURL(currentFile);
      setImageUrl(url);
      setCurrentTitle(currentFile.name);

      cleanup = () => {
        URL.revokeObjectURL(url);
      };
    }
    // Slider mode with url
    else if (imageUrls.length > 0 && currentIndex >= 0 && currentIndex < imageUrls.length) {
      setImageUrl(imageUrls[currentIndex]);
      setCurrentTitle(imageNames[currentIndex] || `Image ${currentIndex + 1}`);
    }
    // Mode one file object
    else if (file) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setCurrentTitle(fileName || file.name);

      cleanup = () => {
        URL.revokeObjectURL(url);
      };
    }
    // Mode one url
    else if (fileUrl) {
      setImageUrl(fileUrl);
      setCurrentTitle(fileName || title);
    }

    return cleanup;
  }, [open, currentIndex, file, fileUrl, fileName, title]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  const handleNext = useCallback(() => {
    if (currentIndex < totalImages - 1) {
      onIndexChange(currentIndex + 1);
    }
  }, [currentIndex, totalImages, onIndexChange]);

  useEffect(() => {
    if (!open || !showNavigation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, showNavigation, handlePrevious, handleNext]);

  const handleDownload = async (): Promise<void> => {
    try {
      // If file is object
      if ((images.length > 0 && images[currentIndex]) || file) {
        const currentFile = images[currentIndex] || file;
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = currentFile!.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // If image from server
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = currentTitle || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(imageUrl, '_blank');
    }
  };

  const modalTitle = (
    <div className='previewModal__title'>
      <span className='previewModal__title-text'>
        {currentTitle || title}
        {showNavigation && ` (${currentIndex + 1}/${totalImages})`}
        <CustomTooltip title='Download'>
        <DownloadOutlined
          onClick={handleDownload}
          className='download-icon'
        />
      </CustomTooltip>
      </span>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={modalTitle}
      centered={true}
      footer={false}
      className="preview-file-modal"
      width="95vw"
    >
      <div className="previewModal">
        {showNavigation && (
          <button
            className={`previewModal__nav previewModal__nav--prev ${currentIndex === 0 ? 'disabled' : ''}`}
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            <LeftOutlined />
          </button>
        )}

        <div className="previewModal__content">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={currentTitle}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </div>

        {showNavigation && (
          <button
            className={`previewModal__nav previewModal__nav--next ${currentIndex === totalImages - 1 ? 'disabled' : ''}`}
            onClick={handleNext}
            disabled={currentIndex === totalImages - 1}
          >
            <RightOutlined />
          </button>
        )}
      </div>
    </Modal>
  );
};
